import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Bootstrapero tonto: V2::ChartComponent arma el hash de opciones completo
// (series + height + baseline de diseño) y lo pasa como único data-value.
// Los colores NO viven en Ruby: se leen de los tokens CSS (--tp-*) para que
// la paleta sea la única fuente de verdad. El caller puede pisarlos vía
// options (deepMerge da prioridad a optionsValue). La capa presenters de
// -1.4 alimentará las series en Phase 2/3. Ver checklist 0.8 / 0.11.
export default class extends Controller {
  static values = {
    options: Object,
    units: Array,
    // Ids de telemetría por instante y URL del historial (tooltip fijo,
    // feedback 10). Vacío en los gráficos que no grafican telemetrías.
    meta: { type: Object, default: {} }
  }

  connect() {
    this._resetting = false
    // Un snapshot de Turbo (volver atrás, o la vista previa de una página ya
    // visitada) trae el SVG que Apex había dibujado, congelado con la paleta
    // del tema de ESE momento. Apex agrega su lienzo sin limpiar el
    // contenedor: el gráfico viejo quedaba encima del nuevo, sin retintar —
    // "el gráfico quedó con los colores oscuros" tras pasar a claro
    // (feedback 2026-09-21, GRA.T4). Se descarta antes de renderizar.
    this.element.replaceChildren()
    this._chart = new ApexCharts(this.element, this.chartOptions)
    this._chart.render()
    // Un contenedor que cambia de alto (pantalla completa) no mueve al gráfico:
    // ApexCharts fija el alto que recibió en las opciones. Este evento le dice
    // cuál usar ahora; sin listener el chart se queda en su alto original
    // dentro de una caja que creció.
    this._onRefit = (event) => this.refit(event.detail?.height)
    window.addEventListener("v2--chart:refit", this._onRefit)
    this._onTheme = () => this.retint()
    window.addEventListener("v2--theme:change", this._onTheme)

    // CSV del menú de descarga: si quien envuelve al gráfico lo resuelve en
    // el servidor (v2--equip-tabs, con las telemetrías reales de la ventana),
    // el de Apex —los puntos muestreados— no corre. Captura: llega antes que
    // el listener que Apex puso en el ítem.
    this._onCsv = (event) => this.interceptCsv(event)
    this.element.addEventListener("click", this._onCsv, true)

    // Tooltip fijo: click sobre el gráfico (no un arrastre de zoom) fija la
    // burbuja del instante bajo el cursor con el link a su telemetría.
    this._onPointerDown = (event) => { this._downAt = [event.clientX, event.clientY] }
    this._onClick = (event) => this.pin(event)
    this._onOutside = (event) => {
      if (this._pinned && !this.element.contains(event.target)) this.unpin()
    }
    this.element.addEventListener("pointerdown", this._onPointerDown)
    this.element.addEventListener("click", this._onClick)
    document.addEventListener("click", this._onOutside)
  }

  disconnect() {
    window.removeEventListener("v2--chart:refit", this._onRefit)
    window.removeEventListener("v2--theme:change", this._onTheme)
    this.element.removeEventListener("click", this._onCsv, true)
    this.element.removeEventListener("pointerdown", this._onPointerDown)
    this.element.removeEventListener("click", this._onClick)
    document.removeEventListener("click", this._onOutside)
    this.unpin()
    if (this._chart) {
      this._chart.destroy()
      this._chart = null
    }
  }

  // `height` null vuelve al alto con el que se renderizó. La toolbar se
  // reubica en el mismo paso: ver `withToolbarOffset`.
  refit(height) {
    if (!this._chart) return

    const options = this.chartOptions
    const chart = { height: height || options.chart?.height }
    const offsetY = options.chart?.toolbar?.offsetY
    if (offsetY !== undefined) chart.toolbar = { offsetY }
    this._chart.updateOptions({ chart }, false, false)
  }

  // Retint en vivo al cambiar de tema (v2--theme): re-lee los tokens --tp-*
  // ya girados por data-theme y los aplica sin reinstanciar el gráfico. Lee
  // en el momento del evento, no antes: getComputedStyle fuerza el recálculo
  // de estilos, así que ve el atributo recién cambiado. El
  // tooltip no hace falta: _chart.scss lo viste con var(--tp-*), que retinta
  // solo con el cambio de atributo.
  retint() {
    if (!this._chart) return

    this._chart.updateOptions(tokenColors(), false, false)
  }

  // Reemplaza series y ejes sin reinstanciar: es lo que usa el selector de
  // rango, que trae otras series para el mismo gráfico. `updateSeries` está
  // roto en ApexCharts v5 (no recalcula eje ni marcadores), de ahí que todo vaya
  // por `updateOptions`.
  replace(series, options = {}, meta = undefined) {
    if (!this._chart) return

    if (meta !== undefined) this.metaValue = meta || {}
    this._chart.updateOptions({ ...this.withToolbarOffset(this.withYAxisCategories(options)), series }, false, false)
  }

  get chartOptions() {
    const options = this.withToolbarOffset(this.withYAxisCategories(this.optionsValue))
    const base = deepMerge(deepMerge(tokenColors(), this.zoomEvents), options)
    return deepMerge(base, this.formatters)
  }

  // Toolbar en pantalla completa (feedback 2026-09-21, GRA.T2): el offset
  // negativo que la sube (V2::EquipChart.toolbar_offset) se come el padding de
  // la card, y en pantalla completa la card se aplana a padding 0 — la toolbar
  // subía por encima del gráfico, contra la fila de tabs. Ruby manda el offset
  // de ese modo en `toolbar.fullscreenOffsetY` (Apex no conoce la clave: acá se
  // saca) y se usa mientras el gráfico viva dentro del elemento a pantalla
  // completa. Pasa en el render, en cada `replace` —trae las opciones de nuevo,
  // con el offset de escritorio— y en `refit`, al entrar y salir.
  withToolbarOffset(options) {
    const toolbar = options.chart?.toolbar
    if (!toolbar || !("fullscreenOffsetY" in toolbar)) return options

    const { fullscreenOffsetY, ...toolbarRest } = toolbar
    const offsetY = document.fullscreenElement?.contains(this.element) ? fullscreenOffsetY : toolbarRest.offsetY
    return { ...options, chart: { ...options.chart, toolbar: { ...toolbarRest, offsetY } } }
  }

  // Eje Y de categorías (gráfico de carga, feedback 2026-09-14): las etiquetas
  // viajan como JSON (`yaxis.labels.categories`, indexadas por valor: 0/1/2 →
  // automático/manual/error) y Apex pide una FUNCIÓN. Acá se convierten, y la
  // clave cruda se saca — Apex no la conoce. Se aplica en el render inicial y
  // en cada `replace`, que trae las opciones de nuevo desde el servidor.
  withYAxisCategories(options) {
    const categories = options.yaxis?.labels?.categories
    if (!Array.isArray(categories) || categories.length === 0) return options

    const labels = new Map(categories.map((text, value) => [value, text]))
    const { categories: _raw, ...labelsRest } = options.yaxis.labels
    return {
      ...options,
      yaxis: { ...options.yaxis, labels: { ...labelsRest, formatter: (value) => labels.get(Number(value)) ?? "" } }
    }
  }

  // Lo único que NO puede venir de Ruby: Apex pide funciones acá y las opciones
  // cruzan como JSON. `tooltip.y` acepta una entrada por serie, así que cada
  // una se lee con su unidad y su precisión ("101.40 psi", "3 golpes") — el
  // formato de V1, que V2 había perdido. La fecha del CSV va por el mismo
  // camino: sin esto Apex la exporta como `toDateString()` y se pierde la hora.
  //
  // `tooltip.x` (feedback 10): la fecha lleva el id de la telemetría del
  // instante, y se anota el punto bajo el cursor para poder fijarlo.
  get formatters() {
    const csv = { dateFormatter: exportTimestamp }
    const tooltip = this.unitsValue.map((unit) => ({ formatter: valueFormatter(unit) }))
    const withSeconds = (this.optionsValue.tooltip?.x?.format || "").includes(":ss")
    const x = {
      formatter: (value) => {
        const point = this.telemetryAt(value)
        this._hover = point
        const date = tooltipTimestamp(value, withSeconds)
        return point ? `${date} · #${point.id}` : date
      }
    }

    return {
      chart: { toolbar: { export: { csv } } },
      tooltip: { x, ...(tooltip.length ? { y: tooltip } : {}) }
    }
  }

  // { id, day, at } de la telemetría dibujada en ese instante, o null (las
  // franjas del golpeteo no son lecturas).
  telemetryAt(at) {
    const entry = this.metaValue.ids?.[String(at)]
    return entry ? { ...entry, at } : null
  }

  interceptCsv(event) {
    if (!event.target.closest?.(".apexcharts-menu-item.exportCSV")) return

    const request = new CustomEvent("v2--chart:csv", { bubbles: true, cancelable: true })
    this.element.dispatchEvent(request)
    if (!request.defaultPrevented) return

    event.stopPropagation()
    event.preventDefault()
    this.element.querySelector(".apexcharts-menu")?.classList.remove("apexcharts-menu-open")
  }

  // Fija la burbuja: copia lo que muestra el tooltip de Apex (fecha, id y
  // valores) en una caja propia que no se va al mover el mouse, con el link
  // al detalle de esa telemetría (solo con permiso: sin `url`, sin link). Un
  // arrastre (zoom) también termina en click: se descarta por distancia.
  pin(event) {
    if (event.target.closest(".tp-chart-pin, .apexcharts-toolbar, .apexcharts-menu")) return
    if (this._downAt) {
      const [x, y] = this._downAt
      if (Math.hypot(event.clientX - x, event.clientY - y) > 4) return
    }

    const point = this._hover
    const source = this.element.querySelector(".apexcharts-tooltip.apexcharts-active")
    if (!point || !source) {
      this.unpin()
      return
    }

    this.unpin()
    const box = document.createElement("div")
    box.className = "tp-chart-pin"
    box.setAttribute("role", "dialog")
    box.innerHTML = source.innerHTML

    const close = document.createElement("button")
    close.type = "button"
    close.className = "tp-chart-pin__close"
    close.setAttribute("aria-label", this.metaValue.labels?.close || "×")
    close.textContent = "×"
    close.addEventListener("click", () => this.unpin())
    box.prepend(close)

    if (this.metaValue.url) {
      const link = document.createElement("a")
      link.className = "tp-chart-pin__link"
      link.href = telemetryHref(this.metaValue.url, point)
      link.textContent = `${this.metaValue.labels?.open || ""} #${point.id}`.trim()
      box.append(link)
    }

    const host = this.element.getBoundingClientRect()
    const anchor = source.getBoundingClientRect()
    box.style.left = `${anchor.left - host.left}px`
    box.style.top = `${anchor.top - host.top}px`
    this.element.append(box)
    this._pinned = box
  }

  unpin() {
    this._pinned?.remove()
    this._pinned = null
  }

  // El zoom de ApexCharts recorta lo que YA está dibujado; acá los puntos
  // vienen downsampleados del servidor, así que acercarse sin volver a pedir
  // solo agranda la misma envolvente. Estos handlers publican la ventana
  // elegida y quien envuelve al gráfico decide si trae esos datos
  // (`v2--equip-tabs`). Van siempre: sin `chart.zoom.enabled` en las opciones
  // no se disparan nunca, así que no cambian nada para el resto de los
  // gráficos V2.
  //
  // RESET (botón de la toolbar): Apex revierte su eje a
  // `globals.initialConfig.xaxis`, y `updateOptions` SOBREESCRIBE esa
  // configuración inicial en cada `replace` — con lo que "initial" termina
  // siendo la ÚLTIMA ventana de zoom y el botón devolvía al estado anterior,
  // no al rango por defecto. Dos partes:
  // 1. `beforeResetZoom` devuelve el eje SIN ventana: es el valor que Apex usa
  //    para su revert local, y anula el "initial" vencido.
  // 2. El reset real es recargar: se emite null/null y quien envuelve pide al
  //    endpoint `#series` sin `from`/`to`, que es el rango completo del
  //    selector. El evento `zoomed` que Apex dispara DESPUÉS con la ventana
  //    vencida se descarta (`_resetting`), o volvía a pedir el zoom recién
  //    deshecho.
  get zoomEvents() {
    return {
      chart: {
        events: {
          zoomed: (_context, { xaxis }) => {
            this.unpin()
            if (this._resetting) {
              this._resetting = false
              return
            }
            this.emitZoom(xaxis?.min, xaxis?.max)
          },
          beforeResetZoom: () => {
            this._resetting = true
            // El `zoomed` vencido llega en el mismo tick; por si Apex cambia
            // el orden, el guardia se desarma solo y no se come un zoom real.
            queueMicrotask(() => { this._resetting = false })
            this.emitZoom(null, null)
            return { xaxis: { min: undefined, max: undefined } }
          }
        }
      }
    }
  }

  // Milisegundos epoch, o null/null para "volvé al rango completo". Burbujea:
  // el que escucha es un ancestro, no este elemento.
  emitZoom(from, to) {
    this.element.dispatchEvent(
      new CustomEvent("v2--chart:zoom", { bubbles: true, detail: { from, to } })
    )
  }
}

// Colores derivados de tokens (paleta = única fuente de verdad). Se leen del
// :root que _semantic.scss expone. `colors`: 1ª serie azul acento, 2ª verde de
// marca (las dos presiones del CA1 deben distinguirse de un vistazo), luego
// ok/warn/critical-text.
function tokenColors() {
  const css = getComputedStyle(document.documentElement)
  const v = (name) => css.getPropertyValue(name).trim()

  return {
    chart: { foreColor: v("--tp-text-muted") },
    colors: [
      v("--tp-chart-line"),
      v("--tp-chart-line-alt"),
      v("--tp-color-ok-text"),
      v("--tp-color-warn-text"),
      v("--tp-color-critical-text")
    ].filter(Boolean),
    grid: { borderColor: v("--tp-chart-grid") }
  }
}

// "101.40 psi", "75.00%", "3 golpes", "Automático". El porcentaje va pegado
// al número (así se escribe, y así lo hacía V1); el resto lleva su espacio.
// `labels` traduce códigos de estado (el sensor de carga del GW2) a su
// etiqueta: el número crudo no se muestra nunca.
function valueFormatter({ unit, decimals, labels }) {
  return (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return ""

    if (labels) {
      const label = labels[String(Math.round(Number(value)))]
      if (label) return label
    }

    const number = Number(value).toFixed(decimals ?? 2)
    if (!unit) return number
    return unit === "%" ? `${number}%` : `${number} ${unit}`
  }
}

// dd-MM-yyyy HH:mm:ss en la hora del navegador: la misma que rotula el eje y el
// tooltip (xaxis.labels.datetimeUTC: false). Sin comas, que son el separador
// del CSV.
function exportTimestamp(timestamp) {
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return timestamp

  const pad = (part) => String(part).padStart(2, "0")
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

// Fecha del tooltip en la hora del navegador, como el eje: con segundos para
// una lectura, sin ellos para una franja agregada (golpeteo).
function tooltipTimestamp(timestamp, withSeconds) {
  const stamp = exportTimestamp(timestamp)
  return withSeconds || typeof stamp !== "string" ? stamp : stamp.slice(0, -3)
}

// Historial del sensor con el detalle de la telemetría abierto y el día de la
// lectura como período (mismo link que el de la telemetría de una alerta).
function telemetryHref(base, { id, day }) {
  const url = new URL(base, window.location.origin)
  url.searchParams.set("from", day)
  url.searchParams.set("to", day)
  url.searchParams.set("row", id)
  return `${url.pathname}${url.search}`
}

function isPlainObject(o) {
  return o !== null && typeof o === "object" && !Array.isArray(o)
}

// arrays y escalares: gana override; objetos: merge recursivo.
function deepMerge(base, override) {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override === undefined ? base : override
  }

  const out = { ...base }
  for (const key of Object.keys(override)) {
    out[key] = key in out ? deepMerge(out[key], override[key]) : override[key]
  }
  return out
}
